import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { i18, trb } from 'src/Utilities';
import { Typography } from 'src/shared';
import type { ILoginForm } from 'src/Containers';
import { UACActions, UACConstants } from 'src/Redux';
import { routerPath } from 'src/Config';
import { WaitingResponseLoader, BackButton } from './';

const { Text } = Typography;

interface IProps {
  data: ILoginForm;
  infoText: string;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  title: string;
  subTitle: string;
}

const TIMEOUT = 60;

export const ConfirmationBox: React.FC<IProps> = ({ data, infoText, setStep, title, subTitle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const language = useSelector((state: any) => state.languageReducer.language);

  const [timer, setTimer] = useState(TIMEOUT);
  const isCheckingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleBack = useCallback(() => {
    setStep((prev) => prev - 1);
  }, [setStep]);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const checkLogin = useCallback(async () => {
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      const result = await dispatch(
        (UACActions as any).postCheckLogin({
          Language: language,
          LoginCode: data.LoginCode,
          LoginSystem: data.LoginType,
          LoginToken: data.LoginSession,
        })
      );

      const statusCode = result?.error?.data?.LoginStatusCode || result?.payload?.LoginStatusCode;

      if (statusCode) {
        cleanup();
        handleBack();
      } else if (result?.type === UACConstants.POST_UAC_CHECK_LOGIN_SUCCESS && result.payload?.IsAuth) {
        cleanup();
        navigate(routerPath.index);
      }
    } catch (error) {
      console.error('Login check failed:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [dispatch, language, data, navigate, handleBack, cleanup]);

  useEffect(() => {
    if (timer <= 0) {
      cleanup();
      dispatch({
        type: UACConstants.POST_UAC_CHECK_LOGIN_ERROR,
        error: { data: { LoginStatusCode: 'loginError.timeout' } },
      });
      handleBack();
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimer((prevTimer) => prevTimer - 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timer, dispatch, handleBack, cleanup]);

  useEffect(() => {
    const checkLoginInterval = setInterval(() => {
      void checkLogin();
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      clearInterval(checkLoginInterval);
    };
  }, [checkLogin]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return (
    <>
      <BackButton handleBackButtonClick={handleBack} />
      <div>
        <Typography.Title className="login_title my-0">{trb(title)}</Typography.Title>
      </div>
      <div>
        <Text className="login_subTitle">{trb(subTitle)}</Text>
      </div>
      <div className="login_form_wrapper">
        <div className="confirmation_wrapper">
          <div>
            <Text className="confirmation_title d-flex justify-content-center">{trb(i18.Labels.AuthenticationCode)}</Text>
          </div>
          <div>
            <Text className="confirmation_code d-flex justify-content-center mb-0">{data.LoginControlCode}</Text>
          </div>
        </div>
        <div className="d-flex justify-content-center mb-5">
          <Text className="login_infoText">{infoText}</Text>
        </div>
      </div>
      <WaitingResponseLoader />
    </>
  );
};
